import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, ShoppingCart, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { useProduct } from '@/hooks/useProducts';
import { useCartStore } from '@/stores/cart-store';
import { useUIStore } from '@/stores/ui-store';
import { cn, formatCurrency } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Skeleton from '@/components/ui/Skeleton';

interface QuickViewModalProps {
  slug: string;
  onClose: () => void;
}

export default function QuickViewModal({ slug, onClose }: QuickViewModalProps) {
  const { t } = useTranslation('product');
  const { data: product, isLoading } = useProduct(slug);
  const addItem = useCartStore((s) => s.addItem);
  const addToast = useUIStore((s) => s.addToast);
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [imageIdx, setImageIdx] = useState(0);
  const [isAdding, setIsAdding] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const variants = product?.variants || [];
  const selectedVariant = variants[selectedVariantIdx];
  const images = product?.images || [];
  const displayImages = images.slice(0, 3);
  const price = selectedVariant?.price ?? product?.base_price ?? 0;

  // Close on Escape
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Focus trap
  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  async function handleAddToCart() {
    if (!selectedVariant) return;
    setIsAdding(true);
    try {
      for (let i = 0; i < quantity; i++) {
        await addItem(selectedVariant.id);
      }
      addToast(t('quickView.addedToCart'), 'success');
      onClose();
    } catch {
      addToast(t('quickView.addFailed'), 'error');
    } finally {
      setIsAdding(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={product?.name || t('quickView.ariaLabel')}
        tabIndex={-1}
        className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-surface rounded-xl shadow-2xl"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-cta-fg/80 hover:bg-cta-fg text-cta hover:text-cta-deep transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
          aria-label={t('quickView.close')}
        >
          <X className="h-5 w-5" />
        </button>

        {isLoading ? (
          <div className="p-8 space-y-4">
            <Skeleton className="aspect-square w-full rounded-lg" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-8 w-1/4" />
          </div>
        ) : product ? (
          <div className="sm:flex">
            {/* Image section */}
            <div className="relative sm:w-1/2 aspect-square bg-linen">
              {displayImages.length > 0 ? (
                <>
                  <img
                    src={displayImages[imageIdx]?.url}
                    alt={product.name}
                    className="h-full w-full object-cover"
                  />
                  {displayImages.length > 1 && (
                    <>
                      <button
                        onClick={() => setImageIdx((i) => (i - 1 + displayImages.length) % displayImages.length)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-cta-fg/80 hover:bg-cta-fg text-cta shadow"
                        aria-label={t('quickView.previousImage')}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setImageIdx((i) => (i + 1) % displayImages.length)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-cta-fg/80 hover:bg-cta-fg text-cta shadow"
                        aria-label={t('quickView.nextImage')}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="h-full w-full flex items-center justify-center text-sage-300">
                  {t('quickView.noImage')}
                </div>
              )}
            </div>

            {/* Details section */}
            <div className="sm:w-1/2 p-6 flex flex-col">
              <h2 className="font-display text-xl font-bold text-sage-900">{product.name}</h2>
              <p className="mt-2 text-2xl font-semibold text-sage-800">{formatCurrency(price)}</p>

              {product.short_description && (
                <p className="mt-3 text-sm text-sage-600 line-clamp-3">{product.short_description}</p>
              )}

              {/* Variant selector */}
              {variants.length > 1 && (
                <div className="mt-4">
                  <label className="text-sm font-medium text-sage-700">{t('quickView.optionLabel')}</label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {variants.map((v: any, idx: number) => (
                      <button
                        key={v.id}
                        onClick={() => { setSelectedVariantIdx(idx); setQuantity(1); }}
                        className={cn(
                          'px-3 py-1.5 text-sm rounded-lg border transition-colors',
                          idx === selectedVariantIdx
                            ? 'border-cta bg-cta text-cta-fg'
                            : 'border-sage-300 text-sage-700 hover:border-sage-500'
                        )}
                      >
                        {v.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity */}
              <div className="mt-4 flex items-center gap-3">
                <label className="text-sm font-medium text-sage-700">{t('quickView.qtyLabel')}</label>
                <div className="flex items-center border border-sage-300 rounded-lg">
                  <button
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="px-3 py-1.5 text-sage-600 hover:text-sage-900"
                    aria-label={t('quickView.decreaseQty')}
                  >
                    -
                  </button>
                  <span className="px-3 py-1.5 text-sm font-medium text-sage-900 min-w-[2rem] text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity((q) => q + 1)}
                    className="px-3 py-1.5 text-sage-600 hover:text-sage-900"
                    aria-label={t('quickView.increaseQty')}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 space-y-3 mt-auto">
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={handleAddToCart}
                  isLoading={isAdding}
                  disabled={!selectedVariant}
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  {t('quickView.addToCart')}
                </Button>
                <Link
                  to={`/product/${product.slug}`}
                  onClick={onClose}
                  className="flex items-center justify-center gap-1.5 text-sm font-medium text-sage-600 hover:text-sage-800 transition-colors"
                >
                  {t('quickView.viewFullDetails')}
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-sage-500">{t('quickView.notFound')}</div>
        )}
      </div>
    </div>
  );
}
